import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { chunk } from 'lodash';

import { NumberOfPairs } from 'constants/marketTypes';
import { getIsMobile } from 'redux/modules/appConfigs/selectors';
import { getCurrentBetHandicapByOfferId, getCurrentBetSelectionIdByOfferId } from 'redux/modules/currentBets/selectors';
import { getMobileEditOfferId } from 'redux/modules/myBets/selectors';
import { IMarket } from 'types/markets';

export default function useMobileUnmatchedEditOfferLine({
  market,
  isMarketClosed
}: {
  market: IMarket;
  isMarketClosed: boolean;
}) {
  const isMobile = useSelector(getIsMobile);
  const mobileEditOfferId = useSelector(getMobileEditOfferId);
  const mobileEditOfferSelectionId = useSelector(
    getCurrentBetSelectionIdByOfferId(isMobile && mobileEditOfferId ? mobileEditOfferId : undefined)
  );
  const mobileEditOfferHandicap = useSelector(
    getCurrentBetHandicapByOfferId(isMobile && mobileEditOfferId ? mobileEditOfferId : undefined)
  );

  const [mobileEditOfferLineState, setMobileEditOfferLineState] = useState<number | null>(null);

  const { group } = NumberOfPairs[market.description.marketType as keyof typeof NumberOfPairs] || NumberOfPairs.default;

  const mobileEditOfferLine = useMemo(() => {
    if (isMarketClosed || !mobileEditOfferHandicap || !mobileEditOfferSelectionId || !isMobile) {
      return null;
    }

    return chunk(market.runners, group).findIndex(linesGroup => {
      return linesGroup.some(
        line =>
          line.selectionId === mobileEditOfferSelectionId &&
          (Number(mobileEditOfferHandicap) || 0) === +(line.handicap || 0)
      );
    });
  }, [market.runners, group, mobileEditOfferSelectionId, mobileEditOfferHandicap, isMarketClosed, isMobile]);

  const isMobileEditOfferLine = isMobile && !!mobileEditOfferLine && mobileEditOfferLine >= 0;

  useEffect(() => {
    if (isMobileEditOfferLine) {
      setMobileEditOfferLineState(mobileEditOfferLine);
    }
  }, [isMobileEditOfferLine]);

  return { mobileEditOfferLineState, isMobileEditOfferLine, mobileEditOfferLine };
}
